import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const Ico = ({ name, ...p }) => { const C = Icons[name] || Icons.Box; return <C {...p} />; };

export default function Tenants() {
  const [mods, setMods] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true); setErr(false);
    api.get("/modules").then(({ data }) => setMods(data)).catch(() => setErr(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = mods.filter((m) =>
    m.name.toLowerCase().includes(q.toLowerCase()) || (m.description || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Tenants — Application Directory"
        subtitle="Central directory of every ERP module. Launch any application you are authorized to access." />

      <div className="relative max-w-sm mb-5">
        <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search modules…" className="pl-9" data-testid="tenants-search" />
      </div>

      {loading && <div className="text-slate-400 py-16 text-center" data-testid="tenants-loading">Loading module directory…</div>}
      {err && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700 flex items-center justify-between" data-testid="tenants-error">
          <span className="flex items-center gap-2"><Icons.AlertTriangle className="h-4 w-4" /> Couldn't load the module directory.</span>
          <Button variant="outline" size="sm" onClick={load}>Retry</Button>
        </div>
      )}
      {!loading && !err && filtered.length === 0 && <div className="text-slate-400 py-16 text-center" data-testid="tenants-empty">No modules match "{q}".</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tenants-grid">
        {filtered.map((m) => (
          <div key={m.module_id} className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col hover:shadow-md hover:border-slate-300 transition-all" data-testid={`tenant-card-${m.module_id}`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-lg bg-slate-900 flex items-center justify-center"><Ico name={m.icon} className="h-5 w-5 text-white" /></div>
              <Badge className={`${m.status === "operational" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} hover:opacity-100 text-[10px]`}>{m.status === "coming_soon" ? "Coming soon" : m.status}</Badge>
            </div>
            <div className="mt-3 font-semibold text-slate-900">{m.name}</div>
            <div className="text-sm text-slate-500 mt-1 flex-1">{m.description}</div>
            <div className="flex items-center justify-between mt-4 text-[11px] text-slate-400">
              <span>v{m.version}</span>{m.updated_at && <span>Updated {fmtDate(m.updated_at)}</span>}
            </div>
            <Button onClick={() => m.route && navigate(m.route)} disabled={!m.authorized || m.coming_soon}
              className="mt-3 bg-slate-900 hover:bg-slate-800 w-full" data-testid={`tenant-launch-${m.module_id}`}>
              {m.coming_soon ? <><Icons.Clock className="h-4 w-4 mr-1.5" /> Coming Soon</>
                : m.authorized ? <><Icons.ArrowUpRight className="h-4 w-4 mr-1.5" /> Open</>
                : <><Icons.Lock className="h-4 w-4 mr-1.5" /> No Access</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
